import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ProductWithMultipleLadders } from '../../../classes/customer-segment/adoption-ladder.class';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { DeviceService } from '@omni/services/device/device.service';
import { Subject, fromEvent } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';
import {
  TrendChartLineStyleType,
  TrendChartSeries,
  TrendChartWindowMode,
} from '../../shared/ind-trend-chart/ind-trend-chart.types';
import {
  RX_ALL_PRODUCTS_ID,
  RX_TOTAL_SERIES_KEY,
  buildAllProductsView,
  buildMetricValues,
  maxStoredBucketCount,
  resolveRxSeriesKey,
} from './rx-trends.aggregate';
import {
  RX_TRENDS_DEFAULT_WINDOW_SIZE,
  RxMetricKey,
  RxPeriodMode,
  RxProductSeries,
  RxTrendTableColumn,
  RxTrendTableRow,
  RxTrendsView,
  roundRxValueForDisplay,
} from './rx-trends.types';

/**
 * Series colours, matching the agreed mock (TRx green, NRx blue, Refills
 * purple). Literals rather than CSS custom properties because the chart's
 * canvas renderer paints these directly and cannot resolve `var()`.
 */
const METRIC_COLORS: Record<RxMetricKey, string> = {
  TRx: 'rgb(22, 163, 74)',
  NRx: 'rgb(37, 99, 235)',
  Refills: 'rgb(139, 92, 246)',
};

/** Legend order, which is also the chart's series order. */
const METRIC_ORDER: RxMetricKey[] = ['TRx', 'NRx', 'Refills'];

/** The snapshot widget's keys, so both surfaces relabel together. */
const METRIC_LABEL_KEYS: Record<RxMetricKey, string> = {
  TRx: 'SALES_DATA_TRX',
  NRx: 'SALES_DATA_NRX',
  Refills: 'SALES_DATA_REFILLS',
};

/**
 * Per-product palette for the All view, assigned by position in the series
 * list. That list is sorted by product name at ingest, so a product keeps its
 * colour across period, metric and view switches.
 *
 * Every product is charted the same way — a solid line in its own colour,
 * competitors included. Colour is the only thing that identifies a product
 * here, so the palette runs long enough to keep neighbours distinguishable
 * before it has to repeat.
 */
const PRODUCT_COLORS = [
  'rgb(37, 99, 235)',
  'rgb(234, 88, 12)',
  'rgb(139, 92, 246)',
  'rgb(22, 163, 74)',
  'rgb(219, 39, 119)',
  'rgb(13, 148, 136)',
  'rgb(202, 138, 4)',
  'rgb(8, 145, 178)',
  'rgb(30, 58, 138)',
  'rgb(2, 132, 199)',
  'rgb(217, 70, 70)',
  'rgb(101, 163, 13)',
  'rgb(146, 64, 14)',
  'rgb(91, 33, 182)',
  'rgb(162, 28, 175)',
];

const TOTAL_COLOR = 'rgb(38, 38, 38)';

/**
 * How long to wait before showing the skeleton placeholder.
 *
 * The keyed read this covers usually finishes in a few milliseconds, well
 * inside the threshold, so nothing is shown at all. The placeholder is for the
 * cases that miss it — a cold database, a contended device, or the chart's
 * first paint in a modal that is still animating.
 */
const SKELETON_DELAY_MS = 150;

/** Plot heights in px. See `chartHeight`. */
const INLINE_CHART_HEIGHT = 260;
const MODAL_CHART_MAX_HEIGHT = 420;
const MODAL_CHART_MIN_HEIGHT = 200;
const MODAL_CHART_CHROME_HEIGHT = 190;

/** A rotation settles in two or three events; dragging a desktop window emits per frame. */
const RESIZE_SETTLE_MS = 100;

/** One legend chip: a metric in the product view, a product in the All view. */
interface RxLegendEntry {
  key: string;
  label: string;
  color: string;
  lineStyleType: TrendChartLineStyleType;
  visible: boolean;
}

/**
 * Rx Trends card for the journey tab (OMNI-60746).
 *
 * Two shapes, driven by which of the journey tab's pills is active:
 *
 * - **A product** — three lines, one per metric (TRx/NRx/Refills), toggled by
 *   the legend chips.
 * - **All** — one line per product for a single selected metric, plus a dashed
 *   total across our own products.
 *
 * Every interaction after render (period toggle, metric, graph/table, legend
 * chips) is a slice of already-loaded data, so none of them cost I/O.
 */
@Component({
  selector: 'rx-trends',
  templateUrl: './rx-trends.html',
  styleUrls: ['./rx-trends.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RxTrendsComponent implements OnInit, OnChanges, OnDestroy {
  /** All products' series for the current contact. One doc's worth of data. */
  @Input() series: RxProductSeries[] = [];
  /**
   * Which of the journey tab's pills is active: a series `productId`, or
   * `RX_ALL_PRODUCTS_ID` for the per-product comparison. Null when the selected
   * product has no Rx series.
   */
  @Input() selectedProductId: string | null = null;
  /**
   * True when Rx data must not be shown for this contact — PDRP opt-out or the
   * restrict-access flag. Renders the message instead of the chart.
   */
  @Input() restricted = false;
  @Input() restrictionMessage = '';
  /**
   * False when this contact has no series yet *and* the territory ingest is
   * still paging. Shows a syncing state, so an HCP whose page has not landed
   * reads as "not here yet" rather than as an HCP with no prescriptions.
   *
   * Series already on disk are a whole snapshot for this contact, so they keep
   * rendering through an ingest — see the parent's `rxTrendsDataComplete`.
   */
  @Input() isDataComplete = true;
  /** Territory ingest is paging. Inline card only — the modal never re-reads its inputs. */
  @Input() isSyncing = false;
  /**
   * Source extract date behind these figures, already formatted for display.
   *
   * The newest bucket lags the extract — 25 days in the data team's sample — so
   * without this a rep reads a chart ending weeks ago as if it ran to today.
   */
  @Input() dataAsOfDate: string | null = null;
  /** True while the parent's disk read is in flight. */
  @Input() isLoading = false;
  /**
   * True when this instance is the content of the fullscreen modal opened by
   * `openFullscreen()`, rather than the inline card. Swaps the expand button
   * for a close button, enlarges the chart, and drops the card chrome
   * (border/margin) in favour of filling the modal. Never set this directly —
   * it's only ever passed via `componentProps` when the modal is created.
   */
  @Input() isModal = false;
  /**
   * The journey tab's pill row, same array instance the tab renders. Only drawn
   * in the modal — inline, the tab already has it above this card.
   */
  @Input() productPills: ProductWithMultipleLadders[] = [];
  /** Which pill reads as active. Owned locally once the modal takes a tap of its own. */
  @Input() activePillId: string | null = null;
  /**
   * A pill tap. The modal is handed the inline card's own emitter through
   * `componentProps`, so a pick inside it reaches the tab's `selectProduct` and
   * both rows move together.
   */
  @Output() pillSelect = new EventEmitter<ProductWithMultipleLadders>();

  readonly metricOrder = METRIC_ORDER;

  periodMode: RxPeriodMode = 'weekly';
  view: RxTrendsView = 'graph';
  /** The All view charts one metric at a time, since products are the lines. */
  selectedMetric: RxMetricKey = 'TRx';
  /** Product-view legend state, keyed by metric. */
  private metricVisibility: Record<RxMetricKey, boolean> = {
    TRx: true,
    NRx: true,
    Refills: true,
  };
  /** All-view legend state, keyed by product id (and the total's own key). */
  private readonly hiddenSeriesKeys = new Set<string>();

  chartSeries: TrendChartSeries[] = [];
  categories: string[] = [];
  legendEntries: RxLegendEntry[] = [];
  tableColumns: RxTrendTableColumn[] = [];
  tableRows: RxTrendTableRow[] = [];

  /**
   * Whether the skeleton placeholder is currently showing.
   *
   * Deliberately not just `isLoading`: a keyed disk read takes a couple of
   * milliseconds, and a placeholder that appears and disappears within two
   * frames reads as a flicker rather than as progress. See `SKELETON_DELAY_MS`.
   */
  showSkeleton = false;

  /** Placeholder bar heights, as percentages. Fixed, so it never shimmers randomly. */
  readonly skeletonBarHeights = [38, 52, 45, 63, 58, 71, 66, 80, 74, 88];

  /**
   * Set once the chart reports its first paint.
   *
   * The placeholder is held until then, not merely until data arrives — the
   * window between the two is a blank canvas, and skeleton → blank → chart
   * looks like a fault. Sticky for the component's life: later re-renders
   * (pill, period, legend) reuse the initialised instance and are immediate,
   * so re-showing the placeholder for those would itself be the flicker.
   */
  private hasChartPainted = false;
  private skeletonDelayTimer?: ReturnType<typeof setTimeout>;

  /**
   * Swipe-to-pan plus pinch-zoom on weekly, where 26 buckets don't fit at
   * once; monthly's 12 fit without interaction, so it renders the full range.
   */
  chartWindowMode: TrendChartWindowMode = 'none';
  /** Opening window width, in buckets. Undefined means show everything. */
  chartInitialVisibleCategories: number | undefined;

  private readonly destroy$ = new Subject<void>();

  /**
   * Plot height in px. The inline card is fixed; the modal takes what the
   * viewport can spare, because a rotated phone is barely taller than the
   * portrait plot alone and a fixed height would push the axis off-screen
   * instead of using the width rotating was meant to buy.
   */
  get chartHeight(): number {
    if (!this.isModal) return INLINE_CHART_HEIGHT;
    // ponytail: one flat figure for the modal's own chrome (header, pill row,
    // legend, padding, safe areas) rather than measuring it. Measure with a
    // ResizeObserver on the chart area if the stack ever grows a row.
    const available = window.innerHeight - MODAL_CHART_CHROME_HEIGHT;
    return Math.min(MODAL_CHART_MAX_HEIGHT, Math.max(MODAL_CHART_MIN_HEIGHT, available));
  }

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly modalCtrl: ModalController,
    private readonly translate: TranslateService,
    private readonly device: DeviceService,
  ) {}

  get isAllProducts(): boolean {
    return this.selectedProductId === RX_ALL_PRODUCTS_ID;
  }

  /** Arrow property: the chart calls this detached from this instance. */
  tooltipOverflowLabel = (hiddenCount: number): string =>
    this.translate.instant('RX_TRENDS_TOOLTIP_MORE', { count: hiddenCount });

  metricLabel(metric: RxMetricKey): string {
    return this.translate.instant(METRIC_LABEL_KEYS[metric]);
  }

  get hasSeriesForProduct(): boolean {
    return this.isAllProducts ? !!this.series?.length : !!this.activeProduct;
  }

  private get activeProduct(): RxProductSeries | undefined {
    if (this.isAllProducts) return undefined;
    return this.series?.find(
      (product) => product.productId === this.selectedProductId,
    );
  }

  /**
   * Required for the fullscreen modal path, and not redundant with
   * `ngOnChanges`: `ModalController.create({ componentProps })` assigns inputs
   * straight onto the instance, which does NOT trigger `ngOnChanges` — that
   * only fires for template-bound inputs. Without this, the modal opened with
   * empty series and only populated once some interaction happened to call
   * `rebuild()` itself.
   */
  ngOnInit(): void {
    this.rebuild();
    this.updateSkeletonState();

    // Rotating re-answers `chartHeight`, and this view is OnPush, so nothing
    // else would run change detection for it. The chart resizes itself from
    // there via its own ResizeObserver. Driven off `resize` rather than the
    // cordova orientation callback: that one fires before the WebView has
    // reflowed, so `window.innerHeight` still reads the portrait value and the
    // plot stays portrait-tall in a landscape viewport until some other change
    // (switching product) happens to re-read it (OMNI-62437).
    if (this.isModal) {
      fromEvent(window, 'resize')
        .pipe(auditTime(RESIZE_SETTLE_MS), takeUntil(this.destroy$))
        .subscribe(() => this.cd.detectChanges());

      if (this.device.syncInProgress) {
        this.closeFullscreen();
      } else {
        this.device.syncInProgress$
          .pipe(takeUntil(this.destroy$))
          .subscribe((inProgress) => {
            if (inProgress) this.closeFullscreen();
          });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A different pill means a different set of lines, so stale legend
    // selections must not carry across — a hidden product id would otherwise
    // still be hidden if the rep came back to All later.
    if (changes.selectedProductId && !changes.selectedProductId.firstChange) {
      this.hiddenSeriesKeys.clear();
    }
    this.rebuild();
    this.updateSkeletonState();
  }

  ngOnDestroy(): void {
    this.clearSkeletonDelayTimer();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Called by the chart on its first paint. */
  onChartPainted(): void {
    if (this.hasChartPainted) return;
    this.hasChartPainted = true;
    this.updateSkeletonState();
  }

  /**
   * Whether anything is still pending — the read, or the chart's first paint.
   *
   * The table view is exempt from the paint condition: it renders synchronously
   * from the same arrays, so there is nothing to wait for.
   */
  private get isSettling(): boolean {
    if (this.isLoading || this.isAwaitingData) return true;
    if (this.view !== 'graph') return false;
    return this.hasSeriesForProduct && !this.hasChartPainted;
  }

  /**
   * Nothing to show for this product yet, and a reason to expect some.
   *
   * The ingest and the disk read settle independently, so a card can have
   * finished reading (nothing there), then be told an ingest is paging, then
   * finish that with still nothing. Rendered as one waiting state — the
   * skeleton — rather than as three: a placeholder that swaps to a message and
   * back reads as a fault, and the message variant also drops the header
   * toggles, so the card changes height twice on a single open.
   */
  get isAwaitingData(): boolean {
    if (this.restricted || this.hasSeriesForProduct) return false;
    // `isDataComplete` already means "nothing for this contact and a reason to
    // expect some". Deliberately not `isSyncing`: this contact's document can
    // have landed with only some of the products on it, and a product that
    // genuinely has no prescriptions must say so rather than showing a
    // placeholder for as long as the territory keeps paging.
    return !this.isDataComplete;
  }

  /** Data on screen, more of it still landing — a note, not a placeholder. */
  get isRefreshingInBackground(): boolean {
    return !this.restricted && this.hasSeriesForProduct && this.isSyncing;
  }

  /**
   * Shows the placeholder only if things are still pending after the delay.
   *
   * A read that resolves inside the threshold never shows one at all, which is
   * the common case and the desired one.
   */
  private updateSkeletonState(): void {
    if (!this.isSettling) {
      this.clearSkeletonDelayTimer();
      if (this.showSkeleton) {
        this.showSkeleton = false;
        this.cd.markForCheck();
      }
      return;
    }

    if (this.showSkeleton || this.skeletonDelayTimer) return;

    this.skeletonDelayTimer = setTimeout(() => {
      this.skeletonDelayTimer = undefined;
      if (!this.isSettling) return;
      this.showSkeleton = true;
      this.cd.markForCheck();
    }, SKELETON_DELAY_MS);
  }

  private clearSkeletonDelayTimer(): void {
    if (this.skeletonDelayTimer) {
      clearTimeout(this.skeletonDelayTimer);
      this.skeletonDelayTimer = undefined;
    }
  }

  onPeriodModeChange(mode: RxPeriodMode): void {
    if (this.periodMode === mode) return;
    this.periodMode = mode;
    this.rebuild();
  }

  onViewChange(view: RxTrendsView): void {
    if (this.view === view) return;
    this.view = view;
    this.cd.markForCheck();
  }

  onMetricChange(metric: RxMetricKey): void {
    if (this.selectedMetric === metric) return;
    this.selectedMetric = metric;
    this.rebuild();
  }

  onToggleLegendEntry(key: string): void {
    if (this.isAllProducts) {
      if (this.hiddenSeriesKeys.has(key)) {
        this.hiddenSeriesKeys.delete(key);
      } else {
        this.hiddenSeriesKeys.add(key);
      }
    } else {
      const metric = key as RxMetricKey;
      this.metricVisibility = {
        ...this.metricVisibility,
        [metric]: !this.metricVisibility[metric],
      };
    }
    this.rebuild();
  }

  /**
   * Opens this same card, at full screen, as its own modal — useful on a
   * tablet or landscape phone where the inline card has more headroom to
   * spare than the ~26-week x-axis needs. The modal instance is a fresh
   * `RxTrendsComponent` with `isModal: true`; it keeps its own state (period,
   * metric, view, legend toggles) independent of the card behind it, so
   * closing it doesn't need to reconcile anything back.
   */
  async openFullscreen(): Promise<void> {
    const modal = await this.modalCtrl.create({
      component: RxTrendsComponent,
      componentProps: {
        series: this.series,
        selectedProductId: this.selectedProductId,
        restricted: this.restricted,
        restrictionMessage: this.restrictionMessage,
        isDataComplete: this.isDataComplete,
        dataAsOfDate: this.dataAsOfDate,
        isLoading: this.isLoading,
        selectedMetric: this.selectedMetric,
        productPills: this.productPills,
        activePillId: this.activePillId,
        pillSelect: this.pillSelect,
        isModal: true,
      },
      cssClass: 'fullscreen',
    });

    // Phones are portrait-locked from login (OMNI-55556); a time series is the
    // one view here that wants the width, so the lock is lifted for as long as
    // the modal is up and reinstated on the way out. Paired here rather than in
    // the modal instance's own lifecycle so the release and the re-lock cannot
    // drift apart — the lock is global, and this component is not its only
    // caller.
    const shouldReleasePortraitLock = this.device.isNativeApp && this.device.isMobileDevice;
    if (shouldReleasePortraitLock) {
      this.device.unlockDeviceOrientation();
      void modal.onDidDismiss().then(() => this.device.lockDeviceOrientationPotrait());
    }

    await modal.present();
  }

  closeFullscreen(): void {
    this.modalCtrl.dismiss();
  }

  /**
   * A pill tap inside the modal. The tab is told first — it owns `selected` on
   * the shared pill objects — then this instance rebuilds, since its inputs
   * were assigned rather than bound and so never see the change.
   */
  onPillSelect(pill: ProductWithMultipleLadders): void {
    if (pill.id === this.activePillId) return;
    this.pillSelect.emit(pill);
    this.activePillId = pill.id;
    this.hiddenSeriesKeys.clear();
    this.selectedProductId = resolveRxSeriesKey(pill, this.series);
    this.rebuild();
  }

  private rebuild(): void {
    if (this.isAllProducts) {
      this.rebuildAllProductsView();
    } else {
      this.rebuildSingleProductView();
    }
    this.cd.markForCheck();
  }

  /** One line per product for the selected metric, plus the dashed total. */
  private rebuildAllProductsView(): void {
    const view = buildAllProductsView(
      this.series ?? [],
      this.periodMode,
      this.selectedMetric,
    );
    if (!view) {
      this.clearRenderState();
      return;
    }

    this.categories = view.categories;
    this.applyWindowMode();

    const entries: RxLegendEntry[] = view.productLines.map((line, index) => ({
      key: line.productId,
      label: line.productName,
      color: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
      lineStyleType: 'solid',
      visible: !this.hiddenSeriesKeys.has(line.productId),
    }));

    if (view.total) {
      entries.push({
        key: RX_TOTAL_SERIES_KEY,
        label: this.translate.instant('RX_TRENDS_TOTAL'),
        color: TOTAL_COLOR,
        lineStyleType: 'dashed',
        visible: !this.hiddenSeriesKeys.has(RX_TOTAL_SERIES_KEY),
      });
    }

    // Rounded here, after the total has been summed from full-precision values,
    // so the total stays the true one rather than a sum of rounded parts.
    const valuesByKey = new Map<string, (number | null)[]>(
      view.productLines.map((line) => [
        line.productId,
        line.values.map(roundRxValueForDisplay),
      ]),
    );
    if (view.total) {
      valuesByKey.set(RX_TOTAL_SERIES_KEY, view.total.map(roundRxValueForDisplay));
    }

    this.#renderChartAndTableFromEntries(entries, valuesByKey);
  }

  /** Three metric lines for the one selected product. */
  private rebuildSingleProductView(): void {
    const product = this.activeProduct;
    if (!product) {
      this.clearRenderState();
      return;
    }

    this.categories =
      (this.periodMode === 'weekly'
        ? product.weeklyLabels
        : product.monthlyLabels) ?? [];
    this.applyWindowMode();

    // Same fallback as the All view: labels come from the period anchors and
    // values do not, so a series with values but no anchor must still chart.
    const bucketCount = Math.max(
      this.categories.length,
      maxStoredBucketCount([product], this.periodMode),
    );
    // Refills is derived from full-precision TRx/NRx inside buildMetricValues,
    // then rounded — so it cannot drift from the two metrics it sits between.
    const valuesByMetric = new Map<RxMetricKey, (number | null)[]>(
      METRIC_ORDER.map((metric) => [
        metric,
        buildMetricValues(product, this.periodMode, metric, bucketCount).map(
          roundRxValueForDisplay,
        ),
      ]),
    );

    const entries: RxLegendEntry[] = METRIC_ORDER.map((metric) => ({
      key: metric,
      label: this.metricLabel(metric),
      color: METRIC_COLORS[metric],
      lineStyleType: 'solid',
      visible: this.metricVisibility[metric],
    }));

    this.#renderChartAndTableFromEntries(entries, valuesByMetric);
  }

  #renderChartAndTableFromEntries(
    entries: RxLegendEntry[],
    valuesByKey: Map<string, (number | null)[]>,
  ): void {
    this.legendEntries = entries;
    this.chartSeries = entries.map((entry) => ({
      name: entry.label,
      data: valuesByKey.get(entry.key) ?? [],
      color: entry.color,
      visible: entry.visible,
      lineStyleType: entry.lineStyleType,
    }));

    const visibleEntries = entries.filter((entry) => entry.visible);
    this.tableColumns = visibleEntries.map((entry) => ({
      label: entry.label,
      color: entry.color,
      lineStyleType: entry.lineStyleType,
    }));
    this.tableRows = this.buildTableRows(
      this.categories,
      visibleEntries.map((entry) => valuesByKey.get(entry.key) ?? []),
    );
  }

  private clearRenderState(): void {
    this.chartSeries = [];
    this.categories = [];
    this.legendEntries = [];
    this.tableColumns = [];
    this.tableRows = [];
    this.chartWindowMode = 'none';
    this.chartInitialVisibleCategories = undefined;
  }

  /** Both modes get the full range and pan/zoom; only weekly opens narrowed. */
  private applyWindowMode(): void {
    const isWeekly = this.periodMode === 'weekly';
    this.chartWindowMode = 'pan-zoom';
    this.chartInitialVisibleCategories = isWeekly
      ? RX_TRENDS_DEFAULT_WINDOW_SIZE
      : undefined;
  }

  private buildTableRows(
    categories: string[],
    columnValues: (number | null)[][],
  ): RxTrendTableRow[] {
    const rows: RxTrendTableRow[] = categories.map((label, index) => ({
      label,
      values: columnValues.map((values) => values[index] ?? null),
      isLatest: false,
    }));
    rows.reverse();
    if (rows.length) {
      rows[0].isLatest = true;
    }
    return rows;
  }
}
